/* eslint-disable */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useMaterialUIController } from "context";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Import your images
import regularFry from "assets/demo-images/regular-fry.png";
import rawSteak from "assets/demo-images/raw-steak.png";
import rawBroccoli from "assets/demo-images/raw-broccoli.png";
import rawChickenWings from "assets/demo-images/raw-chicken-wings.png";
import garbageClass from "assets/demo-images/none.svg";
import emptyChamber from "assets/demo-images/empty-chamber.png";

import NutritionLabel from "components/NutritionLabel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import DeviceThermostatIcon from "@mui/icons-material/DeviceThermostat";
import PsychologyIcon from "@mui/icons-material/Psychology";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

const FOOD_IMAGES = {
  "regular-fry": regularFry,
  "raw-steak": rawSteak,
  "raw-broccoli": rawBroccoli,
  "raw-chicken-wings": rawChickenWings,
  none: garbageClass,
  "empty-chamber": emptyChamber,
};

// per 100g
const NUTRITION_BASE = {
  "raw-broccoli": {
    calories: 34,
    caloriesFromFat: 3,
    totalFat: 0.4,
    saturatedFat: 0,
    transFat: 0,
    cholesterol: 0,
    sodium: 33,
    totalCarbs: 7,
    dietaryFiber: 2.6,
    sugars: 1.7,
    protein: 2.8,
    vitaminA: 12,
    vitaminC: 148,
    calcium: 4.7,
    iron: 2.8,
  },
  "raw-steak": {
    calories: 250,
    caloriesFromFat: 150,
    totalFat: 17,
    saturatedFat: 6.5,
    transFat: 0,
    cholesterol: 77,
    sodium: 72,
    totalCarbs: 0,
    dietaryFiber: 0,
    sugars: 0,
    protein: 25,
    vitaminA: 0,
    vitaminC: 0,
    calcium: 1.8,
    iron: 14,
  },
  "regular-fry": {
    calories: 312,
    caloriesFromFat: 145,
    totalFat: 16,
    saturatedFat: 2.5,
    transFat: 0,
    cholesterol: 0,
    sodium: 210,
    totalCarbs: 41,
    dietaryFiber: 3.8,
    sugars: 0.3,
    protein: 3.4,
    vitaminA: 0,
    vitaminC: 9,
    calcium: 1.2,
    iron: 1.8,
  },
  "raw-chicken-wings": {
    calories: 203,
    caloriesFromFat: 126,
    totalFat: 8.1,
    saturatedFat: 5.8,
    transFat: 0.1,
    cholesterol: 31,
    sodium: 253,
    totalCarbs: 0,
    dietaryFiber: 0,
    sugars: 0,
    protein: 30.5,
    vitaminA: 0,
    vitaminC: 0,
    calcium: 3.9,
    iron: 0.3,
  },
  none: {
    calories: 0,
    caloriesFromFat: 0,
    totalFat: 0,
    saturatedFat: 0,
    transFat: 0,
    cholesterol: 0,
    sodium: 0,
    totalCarbs: 0,
    dietaryFiber: 0,
    sugars: 0,
    protein: 0,
    vitaminA: 0,
    vitaminC: 0,
    calcium: 0,
    iron: 0,
  },
  "empty-chamber": {
    calories: 0,
    caloriesFromFat: 0,
    totalFat: 0,
    saturatedFat: 0,
    transFat: 0,
    cholesterol: 0,
    sodium: 0,
    totalCarbs: 0,
    dietaryFiber: 0,
    sugars: 0,
    protein: 0,
    vitaminA: 0,
    vitaminC: 0,
    calcium: 0,
    iron: 0,
  },
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-US", {
    timeZone: "America/Los_Angeles",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZoneName: "short",
  });
};

const calculateNutrition = (foodName, weightInKg) => {
  const weightInGrams = weightInKg * 1000;

  // Make sure we have a valid food name that exists in our nutrition database
  const safeFood = NUTRITION_BASE[foodName] ? foodName : "none";
  const baseNutrition = NUTRITION_BASE[safeFood];
  const multiplier = weightInGrams / 100; // since base data is per 100g

  return {
    servingSize: `${weightInGrams.toFixed(1)}g`,
    servingsPerContainer: "1",
    calories: Math.round(baseNutrition.calories * multiplier).toString(),
    caloriesFromFat: Math.round(baseNutrition.caloriesFromFat * multiplier).toString(),
    totalFat: `${(baseNutrition.totalFat * multiplier).toFixed(1)}g`,
    saturatedFat: `${(baseNutrition.saturatedFat * multiplier).toFixed(1)}g`,
    transFat: `${(baseNutrition.transFat * multiplier).toFixed(1)}g`,
    cholesterol: `${Math.round(baseNutrition.cholesterol * multiplier)}mg`,
    sodium: `${Math.round(baseNutrition.sodium * multiplier)}mg`,
    totalCarbs: `${(baseNutrition.totalCarbs * multiplier).toFixed(1)}g`,
    dietaryFiber: `${(baseNutrition.dietaryFiber * multiplier).toFixed(1)}g`,
    sugars: `${(baseNutrition.sugars * multiplier).toFixed(1)}g`,
    protein: `${(baseNutrition.protein * multiplier).toFixed(1)}g`,
    vitaminA: `${Math.round(baseNutrition.vitaminA * multiplier)}%`,
    vitaminC: `${Math.round(baseNutrition.vitaminC * multiplier)}%`,
    calcium: `${Math.round(baseNutrition.calcium * multiplier)}%`,
    iron: `${Math.round(baseNutrition.iron * multiplier)}%`,
  };
};

const formatVlmResult = (vlmResult) => {
  if (typeof vlmResult === "string") {
    return vlmResult;
  }

  // Handle structured VLM output formatting
  if (typeof vlmResult === "object" && vlmResult !== null) {
    try {
      // If it has classification properties, format them nicely
      if (vlmResult.classification || vlmResult.confidence || vlmResult.detected_items) {
        let formatted = "";

        if (vlmResult.classification) {
          formatted += `🍽️ Classification: ${vlmResult.classification}\n`;
        }

        if (vlmResult.confidence) {
          formatted += `📊 Confidence: ${(vlmResult.confidence * 100).toFixed(1)}%\n`;
        }

        if (vlmResult.detected_items && Array.isArray(vlmResult.detected_items)) {
          formatted += `🔍 Detected Items:\n`;
          vlmResult.detected_items.forEach((item, index) => {
            formatted += `  ${index + 1}. ${item.name || item} (${
              item.confidence ? (item.confidence * 100).toFixed(1) + "%" : "N/A"
            })\n`;
          });
        }

        if (vlmResult.description) {
          formatted += `📝 Description: ${vlmResult.description}\n`;
        }

        if (vlmResult.cooking_suggestions) {
          formatted += `👨‍🍳 Cooking Suggestions: ${vlmResult.cooking_suggestions}\n`;
        }

        // Add any other properties that weren't handled above
        Object.keys(vlmResult).forEach((key) => {
          if (
            ![
              "classification",
              "confidence",
              "detected_items",
              "description",
              "cooking_suggestions",
            ].includes(key)
          ) {
            formatted += `${key}: ${JSON.stringify(vlmResult[key])}\n`;
          }
        });

        return formatted.trim();
      }

      // Fallback to formatted JSON
      return JSON.stringify(vlmResult, null, 2);
    } catch (error) {
      return `Error formatting result: ${error.message}`;
    }
  }

  return String(vlmResult);
};

const DetectionCard = ({ deviceId }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [detectionData, setDetectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // VLM Integration state
  const [vlmResult, setVlmResult] = useState(null);
  const [vlmStatus, setVlmStatus] = useState(null);
  const [vlmRequestId, setVlmRequestId] = useState(null);

  useEffect(() => {
    const pollDetectionData = async () => {
      try {
        const response = await fetch(
          `https://int.api.chefaicookware.com/device-service/device/id/${deviceId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch detection data");
        }

        const data = await response.json();
        setDetectionData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    if (deviceId) {
      pollDetectionData();
      const pollInterval = setInterval(pollDetectionData, 5000);
      return () => clearInterval(pollInterval);
    }
  }, [deviceId]);

  // VLM Polling Function
  const pollVlmResult = async (requestId) => {
    try {
      console.log(`🤖 Polling VLM result for request: ${requestId}`);

      // Debug: Check if API key is loaded
      const apiKey = process.env.REACT_APP_RUNPOD_API_KEY;
      console.log(
        `🔑 API Key loaded: ${
          apiKey
            ? "Yes (first 10 chars: " + apiKey.substring(0, 10) + "...)"
            : "No - API key is undefined!"
        }`
      );

      const response = await fetch(`https://api.runpod.ai/v2/4jytvpgwq7zwhf/status/${requestId}`, {
        headers: {
          Authorization: `Bearer ${process.env.REACT_APP_RUNPOD_API_KEY}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        console.error("VLM polling failed:", response.status);
        setVlmStatus("error");
        return;
      }

      const data = await response.json();
      console.log("VLM status response:", data);

      if (data.status === "COMPLETED") {
        setVlmResult(data.output);
        setVlmStatus("completed");
        console.log("✅ VLM Classification Result:", data.output);
      } else if (data.status === "FAILED") {
        setVlmStatus("failed");
        console.error("❌ VLM request failed:", data.error);
      } else if (data.status === "IN_PROGRESS" || data.status === "IN_QUEUE") {
        setVlmStatus("processing");
        // Poll again in 3 seconds
        setTimeout(() => pollVlmResult(requestId), 3000);
      } else {
        console.log(`VLM status: ${data.status}`);
        // Poll again for unknown statuses
        setTimeout(() => pollVlmResult(requestId), 3000);
      }
    } catch (error) {
      console.error("Error polling VLM:", error);
      setVlmStatus("error");
    }
  };

  // VLM Detection Effect
  useEffect(() => {
    // Check if detection data has VLM request ID
    const newVlmRequestId = detectionData?.currentDetection?.metadata?.modelInfo?.vlmRequestId;

    if (newVlmRequestId && newVlmRequestId !== vlmRequestId) {
      console.log("🚀 Starting VLM polling for new request:", newVlmRequestId);
      setVlmRequestId(newVlmRequestId);
      setVlmStatus("processing");
      setVlmResult(null);
      pollVlmResult(newVlmRequestId);
    }
  }, [detectionData, vlmRequestId]);

  if (loading) {
    return (
      <Card>
        <MDBox p={2} display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </MDBox>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <MDBox p={2}>
          <MDTypography variant="h6" color="error">
            Error loading detection data: {error}
          </MDTypography>
        </MDBox>
      </Card>
    );
  }

  const currentDetection = detectionData?.currentDetection;
  const metadata = currentDetection?.metadata;
  const foodName = metadata?.classification?.foodName || "Unknown Item";
  const confidence = metadata?.classification?.confidence || 0;
  const weight = metadata?.weight || 0;
  const cookingParams = metadata?.cookingParams || {};
  const modelInfo = metadata?.modelInfo || {};
  console.log(cookingParams);

  const nutritionData = calculateNutrition(foodName, weight);

  const isUndetectable = foodName === "none";

  const displayTitle = isUndetectable
    ? "Undetectable Food Item"
    : foodName
        .split("-")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

  return (
    <MDBox>
      <Card
        sx={{
          height: "87vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <MDBox p={3} sx={{ height: "100%", overflow: "auto" }}>
          <MDBox display="flex" justifyContent="center" mb={3}>
            <MDTypography variant="h4" fontWeight="medium">
              Current Detection
            </MDTypography>
          </MDBox>

          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            {/* Image Section */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <div
                  className="card-img-top"
                  style={{
                    flex: "0 0 250px",
                    position: "relative",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#f5f5f5", // Light background for better visibility
                  }}
                >
                  {foodName === "none" ? (
                    // Option 1: Display SVG as an <img> with controlled size
                    <img
                      src={FOOD_IMAGES[foodName]}
                      alt="Undetectable Food Item"
                      style={{
                        width: "200px",
                        height: "200px",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    // Option 2: Or if you imported the SVG as a React component
                    // <NoneIcon style={{ width: "200px", height: "200px" }} />
                    // For PNG images, keep the background style approach
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        backgroundImage: `url(${FOOD_IMAGES[foodName]})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    />
                  )}
                </div>

                <MDBox p={2}>
                  <MDTypography variant="h5" mb={2}>
                    {displayTitle}
                  </MDTypography>

                  <Grid container spacing={2} sx={{ fontSize: "1.2rem" }}>
                    <Grid item xs={6}>
                      Cook Temperature:{" "}
                      <strong>
                        {isUndetectable ? "" : `${cookingParams.estimatedCookTemperature || 0}°F`}
                      </strong>
                    </Grid>
                    <Grid item xs={6}>
                      Cook Time:{" "}
                      <strong>
                        {isUndetectable
                          ? ""
                          : `${cookingParams.estimatedCookTime?.toFixed(1) || 0} min`}
                      </strong>
                    </Grid>
                  </Grid>

                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={6} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <FitnessCenterIcon />
                      {isUndetectable ? "" : `${(weight * 1000).toFixed(1)} g`}
                    </Grid>
                    <Grid item xs={6} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <DeviceThermostatIcon />
                      {isUndetectable
                        ? ""
                        : `${
                            Number.isInteger(cookingParams.startingTemperature)
                              ? cookingParams.startingTemperature
                              : Number(cookingParams.startingTemperature).toFixed(1)
                          }°F`}
                    </Grid>
                    <Grid item xs={6} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <PsychologyIcon />
                      {isUndetectable || cookingParams.estimatedCoverage === undefined
                        ? ""
                        : `${cookingParams.estimatedCoverage?.toFixed(1)}%`}
                    </Grid>
                    <Grid item xs={6} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AcUnitIcon />
                      {isUndetectable ? "" : cookingParams.frozen ? "Yes" : "No"}
                    </Grid>
                  </Grid>
                </MDBox>

                <MDBox
                  mt="auto"
                  p={2}
                  sx={{
                    borderTop: "1px solid",
                    borderColor: "grey.300",
                    bgcolor: "grey.100",
                  }}
                >
                  <MDTypography variant="button" color="text" textTransform="uppercase">
                    Model
                  </MDTypography>
                  <MDBox display="flex" alignItems="center" gap={2} mt={1}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        backgroundImage: `url(http://localhost:3001/static/media/CHEFAI-2.c08cb884da6c745fd76c.png)`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }}
                    />
                    <MDBox>
                      <MDTypography variant="body2">
                        {modelInfo?.modelId} v{modelInfo?.version}
                      </MDTypography>
                      {modelInfo?.vlmRequestId && (
                        <MDTypography variant="caption" color="text">
                          VLM ID: {modelInfo.vlmRequestId.slice(-8)}
                        </MDTypography>
                      )}
                    </MDBox>
                  </MDBox>
                </MDBox>

                {/* VLM Analysis Section */}
                {modelInfo?.vlmRequestId && (
                  <MDBox
                    p={2}
                    sx={{
                      borderTop: "1px solid",
                      borderColor: "grey.300",
                      bgcolor:
                        vlmStatus === "completed"
                          ? "#e8f5e8"
                          : vlmStatus === "failed"
                          ? "#ffebee"
                          : vlmStatus === "error"
                          ? "#ffebee"
                          : "#e3f2fd",
                      border: "1px solid",
                      borderColor:
                        vlmStatus === "completed"
                          ? "success.main"
                          : vlmStatus === "failed"
                          ? "error.main"
                          : vlmStatus === "error"
                          ? "error.main"
                          : "info.main",
                    }}
                  >
                    <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                      <SmartToyIcon fontSize="small" />
                      <MDTypography
                        variant="button"
                        sx={{ color: "text.primary", fontWeight: "bold" }}
                        textTransform="uppercase"
                      >
                        VLM Analysis
                      </MDTypography>
                      {vlmStatus === "processing" && <CircularProgress size={16} />}
                      {vlmStatus === "completed" && (
                        <CheckCircleIcon fontSize="small" color="success" />
                      )}
                      {(vlmStatus === "failed" || vlmStatus === "error") && (
                        <ErrorIcon fontSize="small" color="error" />
                      )}
                    </MDBox>

                    {vlmStatus === "processing" && (
                      <MDTypography
                        variant="body2"
                        sx={{ color: "info.main", fontWeight: "medium" }}
                      >
                        🔄 Processing VLM classification...
                      </MDTypography>
                    )}

                    {vlmStatus === "completed" && vlmResult && (
                      <MDBox>
                        <MDTypography
                          variant="body2"
                          sx={{ color: "success.main", fontWeight: "bold" }}
                          mb={1}
                        >
                          ✅ VLM Analysis Complete
                        </MDTypography>
                        <MDBox
                          sx={{
                            fontSize: "0.875rem",
                            backgroundColor: darkMode ? "grey.800" : "white",
                            color: darkMode ? "grey.100" : "grey.900",
                            p: 2,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: "grey.300",
                            maxHeight: "120px",
                            overflow: "auto",
                            fontFamily: "monospace",
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {formatVlmResult(vlmResult)}
                        </MDBox>
                      </MDBox>
                    )}

                    {vlmStatus === "failed" && (
                      <MDTypography
                        variant="body2"
                        sx={{ color: "error.main", fontWeight: "medium" }}
                      >
                        ❌ VLM classification failed
                      </MDTypography>
                    )}

                    {vlmStatus === "error" && (
                      <MDTypography
                        variant="body2"
                        sx={{ color: "error.main", fontWeight: "medium" }}
                      >
                        ⚠️ Error polling VLM result
                      </MDTypography>
                    )}
                  </MDBox>
                )}
              </Card>
            </Grid>

            {/* Nutrition Section */}
            <Grid item xs={12} md={6}>
              <MDBox sx={{ height: "100%", overflow: "auto" }}>
                <NutritionLabel nutritionData={nutritionData} />
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </Card>
    </MDBox>
  );
};

// Add PropTypes validation
DetectionCard.propTypes = {
  deviceId: PropTypes.string.isRequired,
};

export default DetectionCard;
